import { useState, useEffect } from 'react';
import { API } from '@/App';
import axios from 'axios';
import { 
  Sprout, Compass, Handshake, Plane, Globe, 
  Ticket, CalendarCheck, PartyPopper, Megaphone, Crown,
  UserPlus, Users, Network, MessageCircle,
  UsersRound, Building, MapPin, 
  ShoppingBag, Briefcase, UserCheck,
  Trophy, Star, ChevronRight
} from 'lucide-react';
import { cn } from '@/lib/utils';

const ICON_MAP = {
  'seedling': Sprout,
  'compass': Compass,
  'handshake': Handshake,
  'plane': Plane,
  'globe': Globe,
  'ticket': Ticket,
  'calendar-check': CalendarCheck,
  'party-popper': PartyPopper,
  'megaphone': Megaphone,
  'crown': Crown,
  'user-plus': UserPlus,
  'users': Users,
  'network': Network,
  'message-circle': MessageCircle,
  'users-round': UsersRound,
  'building': Building,
  'map-pin': MapPin,
  'shopping-bag': ShoppingBag,
  'briefcase': Briefcase,
  'user-check': UserCheck,
};

function BadgeIcon({ icon, className }) {
  const Icon = ICON_MAP[icon] || Star;
  return <Icon className={className} />;
}

export default function GamificationCard({ userId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const url = userId 
          ? `${API}/gamification/profile/${userId}`
          : `${API}/gamification/profile`;
        const { data } = await axios.get(url);
        setData(data);
      } catch {
        // Silently fail — gamification is non-critical
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [userId]);

  if (loading || !data) return null;

  const { points, tier, next_tier, badges, badges_earned, badges_total, stats } = data;
  const earnedBadges = badges?.filter(b => b.earned) || [];
  const unearnedBadges = badges?.filter(b => !b.earned) || [];
  const progressPercent = next_tier 
    ? ((points - (tier?.min_points || 0)) / (next_tier.tier.min_points - (tier?.min_points || 0))) * 100
    : 100;

  return (
    <div className="space-y-4" data-testid="gamification-card">
      {/* Tier & Points */}
      <div className="bg-gradient-to-r from-primary/5 to-accent/5 rounded-2xl p-5 border-2 border-primary/10">
        <div className="flex items-center gap-4 mb-3">
          <div 
            className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg"
            style={{ background: `linear-gradient(135deg, ${tier?.color || '#7c3aed'}, ${tier?.color || '#7c3aed'}dd)` }}
          >
            <BadgeIcon icon={tier?.icon} className="w-7 h-7 text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-bold">{tier?.name || 'Newcomer'}</h3>
              <Trophy className="w-4 h-4 text-amber-500" />
            </div>
            <p className="text-sm text-muted-foreground">{points} points</p>
          </div>
          <div className="text-right">
            <span className="text-2xl font-black" style={{ color: tier?.color }}>{badges_earned}</span>
            <span className="text-sm text-muted-foreground">/{badges_total}</span>
            <p className="text-xs text-muted-foreground">badges</p>
          </div>
        </div>

        {/* Progress bar */}
        {next_tier && (
          <div>
            <div className="flex justify-between text-xs text-muted-foreground mb-1">
              <span>{tier?.name}</span>
              <span>{next_tier.tier.name} — {next_tier.points_needed} pts to go</span>
            </div>
            <div className="h-2 bg-black/5 rounded-full overflow-hidden">
              <div 
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${Math.min(progressPercent, 100)}%`, background: tier?.color || '#7c3aed' }}
              />
            </div>
          </div>
        )}
        {!next_tier && (
          <p className="text-xs text-center text-muted-foreground mt-1">Max tier reached!</p>
        )}
      </div>

      {/* Earned badges */}
      {earnedBadges.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold mb-2">Earned Badges</h4>
          <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
            {earnedBadges.map((badge) => (
              <div
                key={badge.id}
                className="flex flex-col items-center gap-1 p-2 rounded-xl bg-primary/5 border border-primary/10"
                title={`${badge.name}: ${badge.description}`}
              >
                <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                  <BadgeIcon icon={badge.icon} className="w-5 h-5 text-primary" />
                </div>
                <span className="text-[10px] text-center font-medium leading-tight">{badge.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Locked badges — show/hide toggle */}
      {unearnedBadges.length > 0 && !userId && (
        <div>
          <button
            onClick={() => setShowAll(!showAll)}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronRight className={cn("w-4 h-4 transition-transform", showAll && "rotate-90")} />
            {showAll ? 'Hide' : 'Show'} locked badges ({unearnedBadges.length})
          </button>
          {showAll && (
            <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 mt-2">
              {unearnedBadges.map((badge) => (
                <div
                  key={badge.id}
                  className="flex flex-col items-center gap-1 p-2 rounded-xl bg-muted/50 border border-border opacity-50"
                  title={`${badge.name}: ${badge.description}`}
                >
                  <div className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center">
                    <BadgeIcon icon={badge.icon} className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <span className="text-[10px] text-center font-medium leading-tight text-muted-foreground">{badge.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Quick stats */}
      {stats && !userId && (
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="p-2 rounded-xl bg-muted/50">
            <p className="text-lg font-bold">{stats.events_attended || 0}</p>
            <p className="text-[10px] text-muted-foreground">Events</p>
          </div>
          <div className="p-2 rounded-xl bg-muted/50">
            <p className="text-lg font-bold">{stats.connections || 0}</p>
            <p className="text-[10px] text-muted-foreground">Connections</p>
          </div>
          <div className="p-2 rounded-xl bg-muted/50">
            <p className="text-lg font-bold">{stats.cities_joined || 0}</p>
            <p className="text-[10px] text-muted-foreground">Cities</p>
          </div>
        </div>
      )}
    </div>
  );
}
